<template>
    <div class="px-6 py-4 bg-minplan-beige -mb-4">
        <page-title :title="$t('exports.archiveTitle')"></page-title>
    </div>

    <div class="p-6">
        <p class="text-sm text-gray-600 mb-4">{{ $t("exports.archiveDescription") }}</p>

        <div class="flex justify-between items-center mb-2">
            <span class="text-xs uppercase font-semibold tracking-widest text-gray-500">
                {{ project.name }}
            </span>
            <button
                type="button"
                class="text-xs uppercase font-semibold tracking-widest text-minplan-darkgreybeige"
                @click="toggleAll"
            >
                {{
                    allSelected
                        ? $t("exports.archiveDeselectAll")
                        : $t("exports.archiveSelectAll")
                }}
            </button>
        </div>

        <apartment-selection-list
            :apartments="apartments"
            v-model="selected"
        />

        <label class="flex items-center mb-2">
            <input type="checkbox" class="rounded mr-2" v-model="withNotes" />
            <span class="text-sm text-gray-700">{{
                $t("exports.archiveWithNotes")
            }}</span>
        </label>

        <label class="flex items-center">
            <input type="checkbox" class="rounded mr-2" v-model="withChat" />
            <span class="text-sm text-gray-700">{{ $t("exports.archiveWithChat") }}</span>
        </label>

        <p v-if="!selected.length" class="text-xs text-red-600 mt-2">
            {{ $t("exports.archiveNoSelection") }}
        </p>

        <p
            v-if="apartmentsError"
            data-test="archive-field-error"
            class="text-xs text-red-600 mt-2"
        >
            {{ apartmentsError }}
        </p>
    </div>

    <div class="flex justify-end my-4 mx-6">
        <button
            @click="cancel"
            type="button"
            class="inline-block uppercase font-semibold text-xs tracking-widest"
        >
            {{ $t("actions.cancel") }}
        </button>

        <a
            :href="selected.length ? downloadHref : null"
            class="ml-3 inline-flex items-center py-2 px-4 border border-transparent text-xs uppercase tracking-widest font-semibold text-white bg-minplan-greybeige hover:bg-minplan-lightgreybeige focus:outline-none transition"
            :class="{ 'opacity-25 pointer-events-none': !selected.length }"
        >
            {{ $t("actions.download") }}
        </a>
    </div>
</template>

<script>
import { defineComponent } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PageTitle from "@/Shared/Components/PageTitle";
import ApartmentSelectionList from "@/Shared/Components/Apartments/ApartmentSelectionList";

// Welle 3, Thema 12 (Spec
// docs/superpowers/specs/2026-08-04-welle3-thema12-archiv-zip-design.md):
// Auswahl-Modal vor dem ZIP-Download — kein `useForm`/POST (Norm im Repo für
// Exports sind echte `<a href>`-GET-Downloads). Der Download-Link wird
// clientseitig aus der aktuellen Auswahl gebaut; ein Klick ohne Auswahl ist
// über `pointer-events-none`/fehlendes `href` blockiert.
//
// R3 Fix-Runde 1 (B2, Rückbau): ein Axios-Blob-Fetch wurde hier probeweise
// eingeführt, um einen 422 (gelöschte Wohnung in der Auswahl) fassbar zu
// machen — zurückgebaut. Das Archiv-ZIP kann drei- bis vierstellig MB gross
// werden; Welle 3 hat den Server extra auf Streaming umgebaut
// (`downloadZip()`/`deleteFileAfterSend`), genau um ein Puffern im RAM zu
// vermeiden. Ein Blob-Fetch verlagert dieses Puffern in den Browser-Tab und
// widerspricht damit dem Streaming-Entscheid. `ProjectExportController`
// und `ExportProjectArchiveRequest` bleiben bei `<a href>`-GET als Norm.
//
// Der 422-Fall läuft stattdessen über den regulären Inertia-Fehlerkanal: ein
// abgelehnter Request lässt Laravel `redirect()->back()->withErrors(...)`
// laufen, die Meldung landet in der geteilten `errors`-Prop
// (`HandleInertiaRequests`/Inertia-Default) und wird hier über `$page.props
// .errors.apartments` gelesen — ändert der Nutzer danach die Auswahl, wird
// sie verworfen (N7), damit eine neue Auswahl nicht mit der alten Meldung
// verwechselt wird.
export default defineComponent({
    props: ["project", "apartments"],
    inheritAttrs: false,
    emits: ["closeModal"],
    layout: MainLayout,
    components: {
        PageTitle,
        ApartmentSelectionList,
    },
    data() {
        return {
            selected: this.apartments.map((apartment) => apartment.uuid),
            withNotes: false,
            withChat: false,
            errorDismissed: false,
        };
    },
    computed: {
        allSelected() {
            return (
                this.apartments.length > 0 &&
                this.selected.length === this.apartments.length
            );
        },
        downloadHref() {
            return this.route("advisors.projects.archive.export", {
                project: this.project.uuid,
                apartments: this.selected,
                with_notes: this.withNotes,
                with_chat: this.withChat,
            });
        },
        // N7 (R3 Fix-Runde 1): der Redirect-Back landet die Meldung in der
        // geteilten `errors`-Prop — die Auswahl, die sie ausgelöst hat, ist
        // clientseitig noch dieselbe (kein Reload dieser Modal-Instanz).
        // Ändert der Nutzer die Auswahl, gilt die Meldung nicht mehr für die
        // NEUE Auswahl und wird ausgeblendet (`errorDismissed`).
        apartmentsError() {
            if (this.errorDismissed) return null;

            const raw = this.$page.props.errors?.apartments;
            if (!raw) return null;

            return Array.isArray(raw) ? raw[0] : raw;
        },
    },
    watch: {
        selected() {
            this.errorDismissed = true;
        },
    },
    methods: {
        toggleAll() {
            this.selected = this.allSelected
                ? []
                : this.apartments.map((apartment) => apartment.uuid);
        },
        cancel() {
            this.$emit("closeModal");
        },
    },
});
</script>

<style scoped></style>
